import { TSessionConfigData } from 'providers/contexts/CosmosContext';
import { t } from 'i18next';
import { hasProDataInsightsPrivilege } from './privileges';

/**
 *
 * Utility functions to check for certain permissions strings
 * via the /core/session/config user eg (user.site_admin, user.permissions)
 *
 * ------------------------------
 * Related Tickets:
 * CMSADMIN-1585
 * CMSADMIN-1347
 * ------------------------------
 *
 * example:
 * ------------------------------
 * const { sessionConfig } = useCosmos();
 * const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
 *
 * Session Config Data Example
 * {
 *   domain: "example.cms.us-corp-dev-3.vip.tndev.net",
 *   ...
 *   // deprecated string array, prefer `user.permissions`
 *   permissions: ['editorial-asset/main/Full access', ...],
 *   user: {
 *    site_admin: false,
 *    permissions: {
 *      editorial: {
 *       content: {
 *         options: {
 *           'Create assets': true,
 *           'Modify assets': false,
 *           'Delete assets': true,
 *           'Change publish status': false,
 *           'Batch edit search': true,
 *         },
 *       },
 *        workflows: {
 *         options: {
 *           'Change workflow': true,
 *           'Change process': false,
 *         },
 *       },
 *     },
 *   },
 *  },
 * ...
 *
 */

/**
 * @information - Deprecated.
 * OLD list of permissions held by the active user.
 *
 * Deprecated. Use `user.permissions` instead.
 * Backend / blue team is transitioning many of these
 * to the new permissions object structure.
 *
 * In the meantime some functions here will
 * use the old permissions array as needed
 * until the transition is complete.
 *
 */

export type TSessionConfigPermissions =
  | 'full-access'
  | 'access/main/Full access'
  | 'access/main/Edit rules'
  | 'access/main/Edit rules/Create rules'
  | 'access/main/Edit rules/Delete rules'
  | 'access/main/Edit rules/Modify rules'
  | 'access/main/Settings'
  | 'adowl/main/Full access'
  | 'analytics/main/Full access'
  | 'appman/main/Full access'
  | 'broadcast/main/Full access'
  | 'broadcast/main/Limited access/Change settings'
  | 'broadcast/main/Limited access/Read notifications'
  | 'broadcast/main/Limited access/Send notifications'
  | 'calendar/main/Full access'
  | 'camayak/config/Full access'
  | 'classifieds/main/Full access'
  | 'data-insights/main/Full access'
  | 'design-templates/main/Full access'
  | 'disqus/settings/Full access'
  | 'e-editions/main/Full access'
  | 'editorial-asset/main/Assign site tags'
  | 'editorial-asset/main/Change access rules'
  | 'editorial-asset/main/Create keyword tags'
  | 'editorial-asset/main/Edit assets/Batch edit search'
  | 'editorial-asset/main/Edit assets/Change publish status'
  | 'editorial-asset/main/Edit assets/Create assets'
  | 'editorial-asset/main/Edit assets/Delete assets'
  | 'editorial-asset/main/Edit assets/Modify assets'
  | 'editorial-asset/main/Full access'
  | 'editorial-asset/main/Modify configuration/Asset automations'
  | 'editorial-asset/main/Modify configuration/Asset settings'
  | 'editorial-asset/main/Modify configuration/Automated article export'
  | 'editorial-asset/main/Modify configuration/Content importers'
  | 'editorial-asset/main/Modify configuration/Contributed content'
  | 'editorial-asset/main/Modify configuration/DRM policies'
  | 'editorial-asset/main/Modify configuration/Linked sites mapping'
  | 'editorial-asset/main/Modify configuration/Related content styles'
  | 'editorial-asset/main/Modify configuration/Section maps'
  | 'editorial-asset/main/Modify configuration/Semantic analysis'
  | 'editorial-asset/main/Workflows/Change process'
  | 'editorial-asset/main/Workflows/Change workflow'
  | 'editorial-block/main/Full access'
  | 'editorial-comment/main/Full access'
  | 'electionsstats/main/Full access'
  | 'form/asset/Full access'
  | 'form/manager/Full access'
  | 'form/settings_pgp_keys/Full access'
  | 'ftpmanager/settings_ftpmanager/Full access'
  | 'indesign/assignment/Full access'
  | 'indesign/page_creation/Full access'
  | 'indesign/pagetracker/Full access'
  | 'indesign/path_configuration/Full access'
  | 'indesign/translate_rulesets/Full access'
  | 'insights/main/Full access'
  | 'livefyre/settings/Full access'
  | 'mailinglist/main/Full access'
  | 'main/dkim/Full access'
  | 'main/domains/Full access'
  | 'main/field59_upload/Full access'
  | 'main/settings_adstxt/Full access'
  | 'main/settings_applenews/Full access'
  | 'main/settings_auth_consumer/Full access'
  | 'main/settings_auth_provider/Full access'
  | 'main/settings_botblocking/Full access'
  | 'main/settings_crossdomain/Full access'
  | 'main/settings_google/Full access'
  | 'main/settings_integrations/Full access'
  | 'main/settings_jobs/Full access'
  | 'main/settings_oovvuu/Full access'
  | 'main/settings_profanity/Full access'
  | 'main/settings_site/Full access'
  | 'main/settings_tag_geo/Full access'
  | 'main/settings_tag_keyword/Full access'
  | 'main/settings_tag_section/Full access'
  | 'main/settings_tag_site/Full access'
  | 'main/settings_upload/Full access'
  | 'main/settings_urlmap/Full access'
  | 'main/settings_webservice_keys/Full access'
  | 'main/settings_workflow/Full access'
  | 'main/webhooks/Full access'
  | 'marketplace-bannerads/main/Full access'
  | 'marketplace-bannerads/system_tools/Full access'
  | 'marketplace-business/main/Full access'
  | 'media/main/Full access'
  | 'openweb/settings/Full access'
  | 'ott/main/Full access'
  | 'pay/main/Full access'
  | 'pay/main/Full access'
  | 'rondino/settings/Full access'
  | 'staticpages/main/Full access'
  | 'subscription/main/Full access'
  | 'subscription/main/Edit services'
  | 'subscription/main/Edit subscriptions'
  | 'subscription/main/Edit subscriptions/Create subscriptions'
  | 'support/ticket/Full access'
  | 'taxes/settings/Full access'
  | 'upsell/main/Full access'
  | 'user/main/Full access'
  | 'weather/settings_weather/Full access'
  | 'wire/main/Full access'
  | 'wire/main/Import content'
  | 'wire/main/Modify channels'
  | 'wire/main/Modify importers';

/**
 * @information
 * NEW Permissions structure for the active user.
 * Existence of the top level key (eg editorial)
 * indicates access to applicaiton.
 */
export type TSessionConfigUserPermissions = {
  editorial?: {
    content?: {
      options?: {
        'Batch edit search'?: boolean;
        'Change publish status'?: boolean;
        'Create assets'?: boolean;
        'Delete assets'?: boolean;
        'Modify assets'?: boolean;
      };
    };
    workflows?: {
      options?: {
        'Change workflow'?: boolean;
        'Change process'?: boolean;
      };
    };
    site_tags?: {
      options?: {};
    };
    keyword_tags?: {
      options?: {};
    };
    access_rules?: {
      options?: {};
    };
    config?: {
      options?: {
        'Asset automations'?: boolean;
        'Asset settings'?: boolean;
        'Automated article export'?: boolean;
        'Content importers'?: boolean;
        'Contributed content'?: boolean;
        'DRM policies'?: boolean;
        'Linked sites mapping'?: boolean;
        'Related content styles'?: boolean;
        'Section maps'?: boolean;
        'Semantic analysis'?: boolean;
      };
    };
  };
  user?: {
    accounts?: {
      options?: {
        'Assign groups'?: boolean;
        'Change authentication'?: boolean;
        'Change avatar'?: boolean;
        'Edit custom properties'?: boolean;
        'Edit profile'?: boolean;
        'Override concurrent login rules'?: boolean;
      };
    };
    authentication?: {
      options?: {};
    };
    groups?: {
      options?: {};
    };
    profiles?: {
      options?: {};
    };
    blocking?: {
      options?: {};
    };
    oidc?: {
      options?: {};
    };
    saml?: {
      options?: {};
    };
  };
  // should be used after AC-277
  access?: {
    rule?: {
      options?: {
        'Create rules'?: boolean;
        'Delete rules'?: boolean;
        'Modify rules'?: boolean;
      };
    };
    settings?: {
      options?: {};
    };
  };
  // should be used after SUBSCRIPTION-557725
  subscription?: {
    subscription?: {
      options?: {
        'Create Subscription'?: boolean;
      };
    };
    service_modify?: {
      options?: {};
    };
  };
  // Core design and blocks permissions
  core?: {
    blocks?: {
      options?: {};
    };
    blocks_modify?: {
      options?: {
        'Delete blocks'?: boolean;
        'Modify block layout'?: boolean;
        'Modify block settings'?: boolean;
        'Modify sticky assets'?: boolean;
      };
    };
  };
  // Dashboards.
  insights?: {
    dashboards: {
      options?: {
        'Create dashboards'?: boolean;
        'Delete dashboards'?: boolean;
        'Modify dashboards'?: boolean;
        'Share dashboards'?: boolean;
        'Full access'?: boolean;
      };
    };
  };
};

/**
 * Session Config User Data
 * sessionConfig.user
 */
export type TSessionConfigUserData = {
  /** @description Whether the active user has full site access. */
  site_admin?: boolean;
  permissions?: TSessionConfigUserPermissions;
};

/**
 * Permission Details
 *
 * Using object rather than just boolean
 * so we may extend with more details.
 *
 */

type PermissionDetails = {
  granted: boolean; // if the user has the permission
  name: string; // friendly permission name for ui
};

/**
 * Permissions UI States
 *
 * In most cases, only disabled & info are used
 * but also extending with optional href and hidden
 * properties for additional usage in combination
 * permissions checks inside at the component level.
 *
 */
export type PermissionInfo = {
  disabled: boolean; // enable/disable elements
  info?: string | undefined; // maybe tooltip infotext
  hidden?: boolean; // maybe hide the element
  href?: string; // maybe href to help link, etc.
};

//--------------------------------------------------
// TOP LEVEL USER
//--------------------------------------------------

/**
 * session user - has site_admin level permissions
 */
export function checkSiteAdminPermission(sc?: TSessionConfigData): PermissionDetails {
  const hasPerm = sc?.user?.site_admin === true;
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.site_admin'),
  };
  return result;
}

//--------------------------------------------------
// EDITORIAL
//--------------------------------------------------

/**
 * editorial - if user has editorial access
 * checking only for key presence - empty object is valid
 */
export function checkEditorialAccessPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(sc?.user?.permissions?.editorial);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.editorial'),
  };
  return result;
}

/**
 * editorial - if user has editorial config (settings)
 * key existence check - sc?.user?.permissions?.editorial?.config
 */
export function checkEditorialSettingsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(sc?.user?.permissions?.editorial?.config);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.editorial_settings'),
  };
  return result;
}

/**
 * editorial - create editorial assets
 */
export function checkCreateAssetsPermission(sc?: TSessionConfigData): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.content?.options?.['Create assets'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.create_assets'),
  };
  return result;
}

/**
 * editorial - if site has blackbird editor access
 */
export function checkBlackbirdEditorPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(sc?.blackbird ?? false);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.blackbird_editor'),
  };
  return result;
}

/**
 * editorial - modify editorial assets.
 */
export function checkModifyAssetsPermission(sc?: TSessionConfigData): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.content?.options?.['Modify assets'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.modify_assets'),
  };
  return result;
}

/**
 * editorial - delete editorial assets
 */
export function checkDeleteAssetsPermission(sc?: TSessionConfigData): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.content?.options?.['Delete assets'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.delete_assets'),
  };
  return result;
}

/**
 * editorial - change publish status
 */
export function checkChangePublishStatusPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.content?.options?.['Change publish status'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.change_publish_status'),
  };
  return result;
}

/**
 * editorial - batch edit search
 */
export function checkBatchEditSearchPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.content?.options?.['Batch edit search'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.batch_edit_search'),
  };
  return result;
}

/**
 * editorial - workflows access (group key exists)
 */
export function checkWorkflowsAccessPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(sc?.user?.permissions?.editorial?.workflows);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.workflows'),
  };
  return result;
}

/**
 * editorial - change workflow
 */
export function checkChangeWorkflowPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.workflows?.options?.['Change workflow'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.change_workflow'),
  };
  return result;
}

/**
 * editorial - change process
 */
export function checkChangeProcessPermission(sc?: TSessionConfigData): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.workflows?.options?.['Change process'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.change_process'),
  };
  return result;
}

/**
 * editorial - access to site tags
 */
export function checkSiteTagsAccessPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(sc?.user?.permissions?.editorial?.site_tags);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.site_tags'),
  };
  return result;
}

/**
 * editorial - access to keyword tags
 */
export function checkKeywordTagsAccessPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(sc?.user?.permissions?.editorial?.keyword_tags);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.keyword_tags'),
  };
  return result;
}

/**
 * editorial - access rules access
 */
export function checkAccessRulesAccessPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(sc?.user?.permissions?.editorial?.access_rules);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.access_rules'),
  };
  return result;
}

/**
 * editorial - use asset automations
 */
export function checkUseAssetAutomationsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.config?.options?.['Asset automations'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.asset_automations'),
  };
  return result;
}

/**
 * editorial - access asset settings
 */
export function checkAccessAssetSettingsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.config?.options?.['Asset settings'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.asset_settings'),
  };
  return result;
}

/**
 * editorial - manage contributed content
 */
export function checkManageContributedContentPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.config?.options?.['Contributed content'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.contributed_content'),
  };
  return result;
}

/**
 * editorial - edit related content styles
 */
export function checkEditRelatedContentStylesPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.config?.options?.['Related content styles'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.related_content_styles'),
  };
  return result;
}

/**
 * editorial - use content importers
 */
export function checkUseContentImportersPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.config?.options?.['Content importers'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.content_importers'),
  };
  return result;
}

/**
 * editorial - manage linked sites
 */
export function checkManageLinkedSitesPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.config?.options?.['Linked sites mapping'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.linked_sites_mapping'),
  };
  return result;
}

/**
 * editorial - use automated article export
 */
export function checkUseAutomatedArticleExportPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.config?.options?.['Automated article export'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.automated_article_export'),
  };
  return result;
}

/**
 * editorial - manage DRM policies
 */
export function checkManageDRMPoliciesPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.config?.options?.['DRM policies'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.drm_policies'),
  };
  return result;
}

/**
 * editorial - semantic analysis permission
 */
export function checkUseSemanticAnalysisPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.config?.options?.['Semantic analysis'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.semantic_analysis'),
  };
  return result;
}

/**
 * editorial - edit section maps permission
 */
export function checkEditSectionMapsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.editorial?.config?.options?.['Section maps'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.section_maps'),
  };
  return result;
}

//--------------------------------------------------
// USER
//--------------------------------------------------

/**
 * user - check if account has user access at all
 * checking only for key presence - empty object is valid
 */
export function checkUserAccessPermission(sc?: TSessionConfigData): PermissionDetails {
  const hasPerm = Boolean(sc?.user?.permissions?.user);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.user'),
  };
  return result;
}

/**
 * user - accounts access (group key exists)
 * legacy nearest equivalent: user/main/Manage accounts/
 */
export function checkUserAccountPermission(sc?: TSessionConfigData): PermissionDetails {
  const hasPerm = Boolean(sc?.user?.permissions?.user?.accounts);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.manage_user_accounts'),
  };
  return result;
}

/**
 * user - manage groups permission
 * legacy nearest equivalent: user/main/Manage groups
 * note: This is not the same as ASSIGN GROUPS.
 * Site admins have implicit access to manage groups.
 */
export function checkUserManageGroupsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasGroupsPermission = Boolean(sc?.user?.permissions?.user?.groups);
  const hasPerm = hasGroupsPermission;
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.manage_user_groups'),
  };
  return result;
}

/**
 * user - edit profile permission
 */
export function checkEditProfilePermission(sc?: TSessionConfigData): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.user?.accounts?.options?.['Edit profile'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.edit_profile'),
  };
  return result;
}

/**
 * user - change avatar permission
 */
export function checkChangeAvatarPermission(sc?: TSessionConfigData): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.user?.accounts?.options?.['Change avatar'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.change_avatar'),
  };
  return result;
}

/**
 * user - change authentication permission
 */
export function checkChangeAuthenticationPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.user?.accounts?.options?.['Change authentication'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.change_authentication'),
  };
  return result;
}

/**
 * user - override concurrent login rules permission
 */
export function checkOverrideConcurrentLoginRulesPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.user?.accounts?.options?.['Override concurrent login rules'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.override_concurrent_login_rules'),
  };
  return result;
}

/**
 * user - custom properties permission
 */
export function checkEditCustomPropertiesPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.user?.accounts?.options?.['Edit custom properties'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.edit_custom_properties'),
  };
  return result;
}

/**
 * user - assign groups permission
 */
export function checkUserAssignGroupsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(
    sc?.user?.permissions?.user?.accounts?.options?.['Assign groups'],
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.assign_user_groups'),
  };
  return result;
}

/**
 * @information - Deprecated permissions strings array section.
 *
 * TODO: Update functions and tests after SUBSCRIPTION-557725 (subscription)
 * TODO: Update functions and tests after SYNDICATE-553315 (syndication)
 * TODO: Update functions and tests after TNCMS-555958 (broadcast/notifier)
 * TODO: Update functions and tests after MAILLIST-553004 (email reach)
 * TODO: Update functions and tests after AC-277 (access control)
 *
 * NOTE:
 * At some point during dev, some casings of these strings
 * accidentally changed so started making this helper to mitigate,
 * but since all these are going to be migrated to the new format
 * soon this will be unneccessary. In any case, here is an
 * example and helper guide in case we do need to migrate
 * all of these and normalize the strings in the future.
 *
 *
 * ```
 * // Utility types
 * type LowercasePerms = Lowercase<TSessionConfigPermissions>;
 * type LowercasePermsArr = Lowercase<TSessionConfigPermissions>[];
 *
 * // Helper to normalize an array of strings to lowercase.
 *
 * export function lowercaseStringArray<T extends string>(arr: T[]): Lowercase<T>[] {
 *   if (!Array.isArray(arr)) return [];
 *   // remove any non-string then lowercase and cast back to Lowercase<T>
 *   const lowercased = arr
 *       .filter((item): item is T => typeof item === 'string')
 *       .map((item) => item.toLowerCase() as Lowercase<T>)
 *   return lowercased;
 * }
 *
 * // Usage examples with lowercase format
 *
 * export function checkSubscriptionFullAccessPermission(sc?: TSessionConfigData): PermissionDetails {
 *   const oldPerms = sc?.permissions || [];
 *   const lowerPerms = lowercaseStringArray<TSessionConfigPermissions>(oldPerms);
 *   const reqPerms: TSessionConfigPermissions[] = ['subscription/main/Full access'];
 *   const lowerReqPerms = lowercaseStringArray<TSessionConfigPermissions>(reqPerms);
 *   const hasPerm = lowerPerms.some((p) => lowerReqPerms.includes(p));
 *   const result: PermissionDetails = { granted: hasPerm, name: t('blox.permissions.subscription_full') };
 *   return result;
 * }
 * ```
 *
 */

//------------------------------------------------------
// SUBSCRIPTION
//------------------------------------------------------

/**
 * SUBSCRIPTION - if they have any access at all
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkHasAnySubscriptionPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = oldPerms.some(
    (perm) =>
      perm === 'subscription/main/Full access' ||
      perm === 'subscription/main/Edit subscriptions' ||
      perm === 'subscription/main/Edit subscriptions/Create subscriptions' ||
      perm === 'subscription/main/Edit services',
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.subscription_any'),
  };

  return result;
}

/**
 * subscription - if user has full access to subscription
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkSubscriptionFullAccessPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('subscription/main/Full access'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.subscription_full'),
  };
  return result;
}

/**
 * subscription - if user has Edit subscriptions access
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkEditSubscriptionPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];

  // TODO: refactor to use new permissions asap SUBSCRIPTION-557725
  const p1 = Boolean(oldPerms?.includes('subscription/main/Edit subscriptions'));
  const p2 = Boolean(
    oldPerms?.includes('subscription/main/Edit subscriptions/Create subscriptions'),
  );
  const hasPerm = Boolean(p1 || p2);

  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.subscription_edit'),
  };

  return result;
}

/**
 * subscription - if user has Create/Edit subscriptions access
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkCreateSubscriptionPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(
    oldPerms?.includes('subscription/main/Edit subscriptions/Create subscriptions'),
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.subscription_create'),
  };
  return result;
}

/**
 * subscription - if user has Edit services access
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkSubscriptionEditServicesPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('subscription/main/Edit services'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.subscription_edit_services'),
  };
  return result;
}

/**
 * subscription - if user has service_modify permission
 * Uses new permissions object: user.permissions.subscription.service_modify
 */
export function checkSubscriptionServiceModifyPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(sc?.user?.permissions?.subscription?.service_modify);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.subscription_service_modify'),
  };
  return result;
}

//------------------------------------------------------
// SYNDICATION
//------------------------------------------------------

/**
 * syndication - if user has full access to syndication
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkSyndicationFullAccessPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('wire/main/Full access'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.full_access'),
  };
  return result;
}

/**
 * syndication - if user has syndication import content access
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkSyndicationImportContentPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('wire/main/Import content'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.import_content'),
  };
  return result;
}

/**
 * syndication - if user has syndication modify channels access
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkSyndicationModifyChannelsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('wire/main/Modify channels'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.modify_channels'),
  };
  return result;
}

/**
 * syndication - if user has syndication modify importers access
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkSyndicationModifyImportersPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('wire/main/Modify importers'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.modify_importers'),
  };
  return result;
}

/**
 * syndication - if they have any access at all
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkHasAnySyndicationPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = oldPerms.some(
    (perm) =>
      perm === 'wire/main/Full access' ||
      perm === 'wire/main/Import content' ||
      perm === 'wire/main/Modify channels' ||
      perm === 'wire/main/Modify importers',
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.syndication'),
  };

  return result;
}

// --------------------------------------------------
// BROADCAST / NOTIFIER
// --------------------------------------------------

/**
 * broadcast - if user has full access to broadcast
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkBroadcastFullAccessPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('broadcast/main/Full access'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.broadcast_full'),
  };
  return result;
}

/**
 * broadcast - if user has change settings access
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkBroadcastChangeSettingsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(
    oldPerms?.includes('broadcast/main/Limited access/Change settings'),
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.broadcast_change_settings'),
  };
  return result;
}

/**
 * broadcast - if user has read notifications access
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkBroadcastReadNotificationsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(
    oldPerms?.includes('broadcast/main/Limited access/Read notifications'),
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.broadcast_read_notifications'),
  };
  return result;
}

/**
 * broadcast - if user has send notifications access
 * endpoints for delete, create, publish, and update all use this permission
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkBroadcastSendNotificationsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(
    oldPerms?.includes('broadcast/main/Limited access/Send notifications'),
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.broadcast_send_notifications'),
  };
  return result;
}

/**
 * broadcast - if they have any permission at all
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkHasAnyBroadcastPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = oldPerms.some(
    (perm) =>
      perm === 'broadcast/main/Full access' ||
      perm === 'broadcast/main/Limited access/Change settings' ||
      perm === 'broadcast/main/Limited access/Read notifications' ||
      perm === 'broadcast/main/Limited access/Send notifications',
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.broadcast'),
  };

  return result;
}

// --------------------------------------------------
// EMAIL REACH (MAILINGLIST)
// --------------------------------------------------

/**
 * mailinglist/main/Full access
 * This seems to be the only permission in blue.
 * We can treat this as the 'has any' perm in this case.
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkHasEmailReachFullAccessPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('mailinglist/main/Full access'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.email_reach_full'),
  };
  return result;
}

// --------------------------------------------------
// ACCESS CONTROL (JOURNEYS)
// --------------------------------------------------

/**
 * Access Control (Journeys)
 * Check if any access control permission is granted
 * for read access to the journeys app as a whole.
 * @information DEPRECATED PERMISSIONS ARRAY
 */

export function checkHasAnyACPermission(sc?: TSessionConfigData): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = oldPerms.some(
    (perm) =>
      perm === 'access/main/Full access' ||
      perm === 'access/main/Edit rules' ||
      perm === 'access/main/Edit rules/Create rules' ||
      perm === 'access/main/Edit rules/Delete rules' ||
      perm === 'access/main/Edit rules/Modify rules' ||
      perm === 'access/main/Settings',
  );
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.access_control'),
  };
  return result;
}

/**
 * Access Control (Journeys)
 * access/main/Full access
 * @information DEPRECATED PERMISSIONS ARRAY
 */

export function checkACFullPermission(sc?: TSessionConfigData): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('access/main/Full access'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.access_control_full'),
  };
  return result;
}

/**
 * Access Control (Journeys)
 *
 * Confusingly named "access/main/Edit rules" in blue
 * this is, in most cases access to read-only level?
 * Actually modifying the rules is one level deeper.
 *
 * @information DEPRECATED PERMISSIONS ARRAY
 */

export function checkACEditRulesPermission(sc?: TSessionConfigData): PermissionDetails {
  const oldPerms = sc?.permissions || [];

  const hasPerm = Boolean(oldPerms?.includes('access/main/Edit rules'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.access_control_edit_rules'),
  };
  return result;
}

/**
 * Access Control (Journeys)
 * create access control rules
 * @information DEPRECATED PERMISSIONS ARRAY
 */

export function checkACCreateRulesPermission(sc?: TSessionConfigData): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('access/main/Edit rules/Create rules'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.access_control_create_rules'),
  };
  return result;
}

/**
 * Access Control (Journeys)
 * delete access control rules
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkACDeleteRulesPermission(sc?: TSessionConfigData): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('access/main/Edit rules/Delete rules'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.access_control_delete_rules'),
  };
  return result;
}

/**
 * Access Control (Journeys)
 * modify access control rules
 * @information DEPRECATED PERMISSIONS ARRAY
 */

export function checkACModifyRulesPermission(sc?: TSessionConfigData): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('access/main/Edit rules/Modify rules'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.access_control_modify_rules'),
  };
  return result;
}

/**
 * Access Control (Journeys)
 *
 * This is access to the entire right hand sprocket
 * in blue that has things such as Access Criteria,
 * Method Groups, Offer Groups, Messages, etc.
 * Note many of these can also be edited via the
 * journey modal so apply restrictions there as well.
 *
 * @information DEPRECATED PERMISSIONS ARRAY
 */
export function checkACSettingsPermission(sc?: TSessionConfigData): PermissionDetails {
  const oldPerms = sc?.permissions || [];
  const hasPerm = Boolean(oldPerms?.includes('access/main/Settings'));
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.access_control_settings'),
  };
  return result;
}

//--------------------------------------------------
// DATA INSIGHTS & CUSTOM DASHBOARDS
//--------------------------------------------------

/**
 * pro data insights & custom dashboards - if user has access to pro data insights
 * requires the Pro/Advanced Dashboards privilege
 */
export function checkHasProDataInsightsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = hasProDataInsightsPrivilege(sc);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.pro_data_insights'),
  };
  return result;
}

export const getDashboardGroupPermissions = (sc?: TSessionConfigData) => {
  if (!sc?.user?.permissions?.insights?.dashboards) {
    return {
      create: { granted: false, name: t('blox.permissions.pro_data_insights_create') },
      delete: { granted: false, name: t('blox.permissions.pro_data_insights_delete') },
      edit: { granted: false, name: t('blox.permissions.pro_data_insights_edit') },
      full: { granted: false, name: t('blox.permissions.pro_data_insights_full') },
      share: { granted: false, name: t('blox.permissions.pro_data_insights_share') },
    };
  }

  const dashboardsGroupPermsObjPath =
    sc?.user?.permissions?.insights?.dashboards?.options;

  return {
    create: {
      granted: Boolean(dashboardsGroupPermsObjPath?.['Create dashboards']),
      name: t('blox.permissions.pro_data_insights_create'),
    },
    delete: {
      granted: Boolean(dashboardsGroupPermsObjPath?.['Delete dashboards']),
      name: t('blox.permissions.pro_data_insights_delete'),
    },
    edit: {
      granted: Boolean(dashboardsGroupPermsObjPath?.['Modify dashboards']),
      name: t('blox.permissions.pro_data_insights_edit'),
    },
    full: {
      granted: Boolean(dashboardsGroupPermsObjPath?.['Full access']),
      name: t('blox.permissions.pro_data_insights_full'),
    },
    share: {
      granted: Boolean(dashboardsGroupPermsObjPath?.['Share dashboards']),
      name: t('blox.permissions.pro_data_insights_share'),
    },
  };
};

export function checkHasDashboardEditPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = getDashboardGroupPermissions(sc).edit.granted;
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.pro_data_insights_edit'),
  };
  return result;
}

export function checkHasDashboardCreatePermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = getDashboardGroupPermissions(sc).create.granted;
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.pro_data_insights_create'),
  };
  return result;
}

export function checkHasDashboardDeletePermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = getDashboardGroupPermissions(sc).delete.granted;
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.pro_data_insights_delete'),
  };
  return result;
}

export function checkHasDashboardSharePermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = getDashboardGroupPermissions(sc).share.granted;
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.pro_data_insights_share'),
  };
  return result;
}

//--------------------------------------------------
// DESIGN BLOCKS
//--------------------------------------------------

/**
 * Blocks - if user has full access to blocks (core.blocks)
 * Full access users can duplicate, copy to site, delete, and modify settings
 */
export function checkCoreBlocksAccessPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(sc?.user?.permissions?.core?.blocks);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.core_blocks_full_access'),
  };
  return result;
}

/**
 * Blocks - if user has blocks_modify permission group
 * Fine-tuned permission users with this group can have specific block actions
 */
export function checkBlocksModifyAccessPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const hasPerm = Boolean(sc?.user?.permissions?.core?.blocks_modify);
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.blocks_modify'),
  };
  return result;
}

/**
 * Blocks - delete blocks permission
 * Users with this permission can delete blocks (or if they have full core blocks access)
 */
export function checkDeleteBlocksPermission(sc?: TSessionConfigData): PermissionDetails {
  const isAdmin = sc?.user?.site_admin === true;
  const hasFullAccess = Boolean(sc?.user?.permissions?.core?.blocks);
  const hasSpecificPerm = Boolean(
    sc?.user?.permissions?.core?.blocks_modify?.options?.['Delete blocks'],
  );
  const hasPerm = isAdmin || hasFullAccess || hasSpecificPerm;
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.delete_blocks'),
  };
  return result;
}

/**
 * Blocks - modify block settings permission
 * Users with this permission can modify block settings (or have full core blocks access)
 */
export function checkModifyBlockSettingsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const isAdmin = sc?.user?.site_admin === true;
  const hasFullAccess = Boolean(sc?.user?.permissions?.core?.blocks);
  const hasSpecificPerm = Boolean(
    sc?.user?.permissions?.core?.blocks_modify?.options?.['Modify block settings'],
  );
  const hasPerm = isAdmin || hasFullAccess || hasSpecificPerm;
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.modify_block_settings'),
  };
  return result;
}

/**
 * Blocks - modify block layout permission
 * Users with this permission can modify block layout (or have full core blocks access)
 */
export function checkModifyBlockLayoutPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const isAdmin = sc?.user?.site_admin === true;
  const hasFullAccess = Boolean(sc?.user?.permissions?.core?.blocks);
  const hasSpecificPerm = Boolean(
    sc?.user?.permissions?.core?.blocks_modify?.options?.['Modify block layout'],
  );
  const hasPerm = isAdmin || hasFullAccess || hasSpecificPerm;
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.modify_block_layout'),
  };
  return result;
}

/**
 * Blocks - modify sticky assets permission
 * Users with this permission can modify sticky assets (or have full core blocks access)
 */
export function checkModifyStickyAssetsPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const isAdmin = sc?.user?.site_admin === true;
  const hasFullAccess = Boolean(sc?.user?.permissions?.core?.blocks);
  const hasSpecificPerm = Boolean(
    sc?.user?.permissions?.core?.blocks_modify?.options?.['Modify sticky assets'],
  );
  const hasPerm = isAdmin || hasFullAccess || hasSpecificPerm;
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.modify_sticky_assets'),
  };
  return result;
}

/**
 * Blocks - full access to core blocks (admin or full permission)
 * Users with full access can duplicate, copy to site, delete, and modify settings
 */
export function checkCoreBlocksFullAccessPermission(
  sc?: TSessionConfigData,
): PermissionDetails {
  const isAdmin = sc?.user?.site_admin === true;
  const hasFullAccess = Boolean(sc?.user?.permissions?.core?.blocks);
  const hasPerm = isAdmin || hasFullAccess;
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.core_blocks_full_access'),
  };
  return result;
}

/**
 * Blocks - create/duplicate blocks permission
 * Only full access users (admins/staff) can create or duplicate blocks
 */
export function checkCreateBlocksPermission(sc?: TSessionConfigData): PermissionDetails {
  const isAdmin = sc?.user?.site_admin === true;
  const hasFullAccess = Boolean(sc?.user?.permissions?.core?.blocks);
  const hasPerm = isAdmin || hasFullAccess;
  const result: PermissionDetails = {
    granted: hasPerm,
    name: t('blox.permissions.create_blocks'),
  };
  return result;
}
